/**
 * Ueber dem Analysefeld einer BWA: ist dieses Monatspaket im Datenbestand,
 * und hat es alle Gegenproben bestanden?
 *
 * Am Anfang wird der Bestand von Hand befuellt, Paket fuer Paket, und dabei
 * geprueft. Deshalb zeigt das Feld nach dem Einlesen genau, was uebernommen
 * wurde und welche Proben gelaufen sind — oder warum das Paket abgelehnt
 * wurde. Ein Paket wird ganz oder gar nicht uebernommen.
 */
import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'

interface Probe {
  name: string
  geprueft: number
  fehler: number
}

interface Einlesung {
  ergebnis: 'uebernommen' | 'abgelehnt'
  leser_fassung: string
  stand: string | null
  jahr: number | null
  monat: number | null
  rechnungslegung: 'handelsrecht' | 'steuerrecht' | null
  rechnungslegung_herkunft: 'kopf' | 'zugeordnet' | 'manuell' | null
  kontenrahmen: string | null
  seitenzahl: number | null
  seitenkarte: { teil: string; seiten: number[] }[]
  proben: Probe[]
  befunde: string[]
  hinweise: string[]
  eingelesen_am: string
  zaehler: Record<'kontobewegungen' | 'kontostaende' | 'positionswerte' | 'zuordnungen' | 'monate', number>
  /** Ergeben die zugeordneten Konten in diesem Monat die gedruckten Positionen? */
  zuordnung: { belegt: number; nicht_belegt: string[]; nicht_pruefbar: number } | null
}

interface Antwort {
  einlesung: Einlesung | null
  leserFassung: string
  /** Pakete desselben Mandanten, die dank dieses Pakets jetzt uebernommen wurden. */
  nachgeholt?: string[]
}

/** Die Teile eines Pakets in der Sprache der Kanzlei. Was nicht gelesen wird, steht nicht da. */
const TEIL_NAME: Record<string, string> = {
  ker: 'Kurzfristige Erfolgsrechnung',
  wertenachweis_ker: 'Wertenachweis',
  wertenachweis_ker_alt: 'Wertenachweis (älteres Format)',
  wertenachweis_jahr: 'Wertenachweis zur Jahresübersicht',
  susa_monat: 'Summen und Salden',
  entwicklung: 'Entwicklungsübersicht',
  bwa_jahr: 'Jahresübersicht',
  vorjahresvergleich: 'Vorjahresvergleich',
  wertenachweis_vorjahr: 'Wertenachweis zum Vorjahresvergleich',
}

const MONATE = ['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember']

function datum(iso: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

const zahl = (n: number) => n.toLocaleString('de-DE')

/**
 * versteckt: Der Monatsbericht darunter traegt die Daten im Kopf. Dann
 * erscheint das Feld nur, wenn es etwas zu tun gibt (nicht uebernommen,
 * aelterer Leser) oder wenn es jemand ueber das Menue einblendet.
 */
export function BwaDatenfeld({ dokumentId, versteckt = false }: { dokumentId: string; versteckt?: boolean }) {
  const kunde = useQueryClient()
  const schluessel = ['bwa-einlesung', dokumentId]

  const abfrage = useQuery({
    queryKey: schluessel,
    queryFn: () => api.holen<Antwort>(`/api/dokumente/${dokumentId}/bwa`),
  })

  const [nachgeholt, setNachgeholt] = useState<string[]>([])
  const einlesen = useMutation({
    mutationFn: (rechnungslegung?: 'handelsrecht' | 'steuerrecht') =>
      api.senden<Antwort>(`/api/dokumente/${dokumentId}/bwa/einlesen`, rechnungslegung ? { rechnungslegung } : {}),
    onSuccess: (antwort) => {
      setNachgeholt(antwort.nachgeholt ?? [])
      kunde.setQueryData(schluessel, antwort)
      void kunde.invalidateQueries({ queryKey: ['dokument', dokumentId] })
      void kunde.invalidateQueries({ queryKey: ['dokumente'] })
    },
  })

  const e = abfrage.data?.einlesung ?? null
  const veraltet = e !== null && abfrage.data !== undefined && e.leser_fassung !== abfrage.data.leserFassung
  const gelesen = e ? [...new Set(e.seitenkarte.map((a) => a.teil).filter((t) => t in TEIL_NAME))] : []
  const proben = e?.proben.filter((p) => p.geprueft > 0) ?? []
  const pruefungen = proben.reduce((a, p) => a + p.geprueft, 0)

  /* Sauber eingelesen ist das Feld Nachschlagewissen und klappt zu, damit die
     Analyse oben steht. Offen bleibt es, wenn es etwas zu tun oder zu lesen
     gibt: noch nicht eingelesen, abgelehnt, mit aelterem Leser. */
  const ruhig = e?.ergebnis === 'uebernommen' && !veraltet
  const [aufgeklappt, setAufgeklappt] = useState<boolean | null>(null)
  const offen = aufgeklappt ?? !ruhig

  // Ueber das Menue eingeblendet: gleich aufgeklappt, sonst saehe man nur die Kopfzeile.
  const warVersteckt = useRef(versteckt)
  useEffect(() => {
    if (warVersteckt.current && !versteckt) setAufgeklappt(true)
    warVersteckt.current = versteckt
  }, [versteckt])

  if (versteckt && ruhig) return null

  return (
    <section className="datenfeld" aria-label="Datenbestand">
      <div className="datenfeld-kopf">
        <div className="datenfeld-titel">
          {ruhig ? (
            <button type="button" className="datenfeld-klapper" aria-expanded={offen} onClick={() => setAufgeklappt(!offen)}>
              <svg width="14" height="14" viewBox="0 0 18 18" aria-hidden="true" className={`winkel${offen ? ' offen' : ''}`}>
                <path d="M6.5 4 L11.5 9 L6.5 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <h2>Daten</h2>
            </button>
          ) : (
            <h2>Daten</h2>
          )}
          {abfrage.isPending && <span className="nebensache">Wird geladen …</span>}
          {!abfrage.isPending && !e && <span className="nebensache">Noch nicht eingelesen</span>}
          {e?.ergebnis === 'uebernommen' && (
            <span className="nebensache">
              <span className="datenfeld-marke gut">Übernommen</span> am {datum(e.eingelesen_am)}
              {veraltet && ' · mit älterem Leser'}
            </span>
          )}
          {e?.ergebnis === 'abgelehnt' && (
            <span className="nebensache">
              <span className="datenfeld-marke schlecht">Abgelehnt</span> am {datum(e.eingelesen_am)}
            </span>
          )}
        </div>
        {offen && <button
          type="button"
          className={`knopf${!e || veraltet ? ' gefuellt' : ''}`}
          disabled={einlesen.isPending || abfrage.isPending}
          onClick={() => einlesen.mutate(undefined)}
        >
          {einlesen.isPending ? 'Wird eingelesen …' : e ? 'Neu einlesen' : 'BWA einlesen'}
        </button>}
      </div>

      {nachgeholt.length > 0 && (
        <p className="datenfeld-zeile datenfeld-block">
          Ebenfalls übernommen, weil jetzt die Rechnungslegung des Mandanten feststeht: {nachgeholt.join(', ')}
        </p>
      )}

      {einlesen.isError && (
        <div className="fehler datenfeld-block">
          {einlesen.error instanceof Error ? einlesen.error.message : 'Das Einlesen hat nicht geklappt.'}
        </div>
      )}
      {abfrage.isError && (
        <div className="fehler datenfeld-block">
          {abfrage.error instanceof Error ? abfrage.error.message : 'Der Stand ist nicht abrufbar.'}
        </div>
      )}

      {offen && e?.ergebnis === 'uebernommen' && (
        <div className="datenfeld-block">
          <p className="datenfeld-zeile">
            {e.monat && e.jahr ? `${MONATE[e.monat - 1]} ${e.jahr}` : ''}
            {e.stand && ` · Buchungsstand ${datum(e.stand)}`}
            {e.rechnungslegung && ` · ${e.rechnungslegung === 'handelsrecht' ? 'Handelsrecht' : 'Steuerrecht'}`}
            {e.rechnungslegung_herkunft === 'zugeordnet' && ' (nicht im Kopf, zugeordnet)'}
            {e.rechnungslegung_herkunft === 'manuell' && ' (nicht im Kopf, von Hand gewählt)'}
            {e.kontenrahmen && ` · ${e.kontenrahmen}`}
          </p>
          <dl className="datenfeld-zahlen">
            <div>
              <dt>Monate</dt>
              <dd>{zahl(e.zaehler.monate)}</dd>
            </div>
            <div>
              <dt>Positionswerte</dt>
              <dd>{zahl(e.zaehler.positionswerte)}</dd>
            </div>
            <div>
              <dt>Kontowerte</dt>
              <dd>{zahl(e.zaehler.kontobewegungen)}</dd>
            </div>
            <div>
              <dt>Kontostände</dt>
              <dd>{zahl(e.zaehler.kontostaende)}</dd>
            </div>
            <div>
              <dt>Konto → Position</dt>
              <dd>{zahl(e.zaehler.zuordnungen)}</dd>
            </div>
          </dl>
          <p className="datenfeld-zeile nebensache">Gelesen: {gelesen.map((t) => TEIL_NAME[t]).join(', ')}</p>
          {e.zuordnung && (
            <p className="datenfeld-zeile nebensache">
              {/* Nur belegte Zuordnungen duerfen eine Position ueber ihre Konten erklaeren. */}
              Konten → Position in diesem Monat:{' '}
              {e.zuordnung.nicht_pruefbar > 0 && e.zuordnung.belegt === 0 && e.zuordnung.nicht_belegt.length === 0
                ? 'nicht prüfbar, zu keinem Stand Kontowerte'
                : `${e.zuordnung.belegt} Positionen belegt`}
              {e.zuordnung.nicht_belegt.length > 0 && `, nicht belegt: ${e.zuordnung.nicht_belegt.join(', ')}`}
            </p>
          )}
        </div>
      )}

      {e?.ergebnis === 'abgelehnt' && (
        <div className="datenfeld-block">
          <p className="datenfeld-zeile">Keine Zahl aus diesem Paket ist im Bestand. Gründe:</p>
          <ul className="datenfeld-liste">
            {e.befunde.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
          {/* Nur wenn allein die Rechnungslegung fehlt: der Berater waehlt sie aus.
              Gespeichert wird, dass die Angabe von Hand kam und von wem. */}
          {e.befunde.length > 0 && e.befunde.every((b) => b.startsWith('Rechnungslegung im Kopf nicht angegeben')) && (
            <div className="datenfeld-wahl">
              <p className="datenfeld-zeile">Das Paket druckt nicht, ob es Handels- oder Steuerrecht ist. Wenn Sie es wissen, wählen Sie es aus:</p>
              <div className="datenfeld-knoepfe">
                <button type="button" className="knopf" disabled={einlesen.isPending} onClick={() => einlesen.mutate('handelsrecht')}>
                  Als Handelsrecht einlesen
                </button>
                <button type="button" className="knopf" disabled={einlesen.isPending} onClick={() => einlesen.mutate('steuerrecht')}>
                  Als Steuerrecht einlesen
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {offen && e && e.hinweise.length > 0 && (
        <ul className="datenfeld-liste datenfeld-block hinweis">
          {e.hinweise.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ul>
      )}

      {offen && proben.length > 0 && (
        <details className="datenfeld-block datenfeld-proben">
          <summary>
            {proben.length} Gegenproben, {zahl(pruefungen)} Einzelprüfungen
            {proben.every((p) => p.fehler === 0) ? ', alle bestanden' : ', nicht alle bestanden'}
          </summary>
          <ul>
            {proben.map((p) => (
              <li key={p.name} className={p.fehler === 0 ? 'gut' : 'schlecht'}>
                <span aria-hidden="true">{p.fehler === 0 ? '✓' : '✗'}</span>
                <span>{p.name}</span>
                <span className="zahl">{zahl(p.geprueft)}</span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  )
}
